import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, PenLine, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, fmtDate } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";
import { useLang } from "@/context/LanguageContext";
import { useSearchableList } from "@/hooks/useSearchableList";
import { PageHeader, EmptyState, CopyButton } from "@/components/shared";
import { FormBuilderDialog } from "@/components/FormBuilderDialog";
import { FormFillDialog } from "@/components/FormFillDialog";

export default function Forms() {
  const { items: templates, reload: reloadTemplates } = useSearchableList("/form_templates");
  const [submissions, setSubmissions] = useState([]);
  const [builder, setBuilder] = useState(undefined); // undefined closed, null new, obj edit
  const [fillTpl, setFillTpl] = useState(null);
  const [viewSub, setViewSub] = useState(null);
  const { t, lang } = useLang();

  const loadSubmissions = useCallback(() => {
    api.get("/form_submissions").then((r) => setSubmissions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);
  useSyncedReload(loadSubmissions);

  const catLabel = {
    seller_details: t("sellerDetailsCat"), property_details: t("propertyDetailsCat"),
    buyer_criteria: t("buyerCriteriaCat"), viewing_feedback: t("viewingFeedbackCat"), custom: t("customCat"),
  };
  const CAT_ORDER = { seller_details: 0, property_details: 1, buyer_criteria: 2, viewing_feedback: 3, custom: 4 };
  const sortedTemplates = [...templates].sort(
    (a, b) => (CAT_ORDER[a.category] ?? 9) - (CAT_ORDER[b.category] ?? 9) || a.name.localeCompare(b.name)
  );

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/form_templates/${tpl.id}`);
    toast.success(t("deleted"));
    reloadTemplates();
  };

  const deleteSubmission = async (sub) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/form_submissions/${sub.id}`);
    toast.success(t("deleted"));
    setViewSub(null);
    loadSubmissions();
  };

  const fmtValue = (v) => {
    if (v.type === "checkbox") return v.value ? "✓" : "—";
    if (v.type === "file") return v.value ? String(v.value).split("/").pop() : "—";
    return v.value === "" || v.value == null ? "—" : String(v.value);
  };

  const summaryText = (sub) =>
    `${sub.template_name}${sub.linked_name ? ` — ${sub.linked_name}` : ""}\n${fmtDate(sub.created_at, lang)}\n\n` +
    (sub.values || []).map((v) => `${v.label}: ${fmtValue(v)}`).join("\n");

  return (
    <div data-testid="forms-page">
      <PageHeader
        title={t("formBuilder")}
        action={
          <button data-testid="new-form-button" onClick={() => setBuilder(null)}
            className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">
            <Plus size={16} strokeWidth={1.5} /> {t("newForm")}
          </button>
        }
      />
      <Tabs defaultValue="templates">
        <TabsList className="bg-stone-100 h-auto p-1 mb-5">
          <TabsTrigger value="templates" data-testid="forms-tab-templates">{t("templatesTab")}</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="forms-tab-submissions">{t("submissionsTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          {templates.length === 0 ? <EmptyState message={t("noResults")} /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {sortedTemplates.map((tpl) => (
                <div key={tpl.id} data-testid={`form-template-card-${tpl.id}`}
                  className="bg-white border border-stone-200 rounded-xl p-5 hover:border-stone-300 transition-all duration-200">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-lg font-medium text-stone-900">{tpl.name}</h3>
                    <span className="bg-stone-100 text-stone-600 border border-stone-200 rounded-full px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap">
                      {catLabel[tpl.category] || tpl.category}
                    </span>
                  </div>
                  <p className="text-xs text-stone-400 mt-1">{(tpl.fields || []).length} {t("addField").split(" ").pop().toLowerCase()}s</p>
                  <div className="flex items-center gap-2 mt-4">
                    <button data-testid={`fill-form-${tpl.id}`} onClick={() => setFillTpl(tpl)}
                      className="inline-flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-4 py-2 text-xs font-medium transition-colors">
                      <FileText size={13} strokeWidth={1.5} /> {t("fillForm")}
                    </button>
                    <button data-testid={`edit-form-${tpl.id}`} onClick={() => setBuilder(tpl)}
                      className="inline-flex items-center gap-1.5 bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-4 py-2 text-xs font-medium transition-colors">
                      <PenLine size={13} strokeWidth={1.5} /> {t("edit")}
                    </button>
                    <button data-testid={`delete-form-${tpl.id}`} onClick={() => deleteTemplate(tpl)}
                      className="ml-auto text-stone-400 hover:text-[#7F1D1D] transition-colors p-1.5">
                      <Trash2 size={15} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="submissions">
          {submissions.length === 0 ? <EmptyState message={t("noSubmissions")} /> : (
            <div className="space-y-2.5">
              {submissions.map((sub) => (
                <button key={sub.id} data-testid={`submission-row-${sub.id}`} onClick={() => setViewSub(sub)}
                  className="w-full text-left flex items-center justify-between gap-4 bg-white border border-stone-200 rounded-xl px-5 py-4 hover:border-stone-300 transition-all duration-200">
                  <div>
                    <p className="text-sm font-medium text-stone-800">{sub.template_name}</p>
                    <p className="text-xs text-stone-400 mt-0.5">
                      {sub.linked_name ? `${sub.linked_name} · ` : ""}{fmtDate(sub.created_at, lang)}
                    </p>
                  </div>
                  <span className="text-xs text-stone-400">{(sub.values || []).length}</span>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {builder !== undefined && (
        <FormBuilderDialog template={builder} onClose={() => setBuilder(undefined)} onSaved={reloadTemplates} />
      )}
      {fillTpl && (
        <FormFillDialog template={fillTpl} onClose={() => setFillTpl(null)} onSaved={loadSubmissions} />
      )}

      <Dialog open={!!viewSub} onOpenChange={(o) => !o && setViewSub(null)}>
        <DialogContent className="max-w-2xl bg-white">
          {viewSub && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-medium text-stone-900">
                  {viewSub.template_name}{viewSub.linked_name ? ` — ${viewSub.linked_name}` : ""}
                </DialogTitle>
              </DialogHeader>
              <div className="flex justify-end">
                <CopyButton text={summaryText(viewSub)} testId="copy-submission-button" label={t("copySummary")} />
              </div>
              <div data-testid="submission-summary" className="bg-stone-50 border border-stone-200 rounded-xl p-5 mt-2 divide-y divide-stone-200">
                {(viewSub.values || []).map((v) => (
                  <div key={v.field_id} className="py-2.5 grid grid-cols-1 sm:grid-cols-3 gap-1">
                    <span className="text-xs uppercase tracking-[0.1em] font-semibold text-stone-500 sm:col-span-1">{v.label}</span>
                    <span className="text-sm text-stone-800 whitespace-pre-wrap sm:col-span-2">{fmtValue(v)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-4">
                <button data-testid="delete-submission-button" onClick={() => deleteSubmission(viewSub)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7F1D1D] hover:underline">
                  <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
                </button>
                <button onClick={() => setViewSub(null)}
                  className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">
                  {t("close")}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
